import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Session, User } from '@supabase/supabase-js';
import { BehaviorSubject } from 'rxjs';
import { SupabaseService } from './supabase.service';

/**
 * Authentication against the Foundation Supabase user pool. Replaces the
 * previous Firebase (Google sign-in) flow. The access token is sent to the
 * billy backend in the `x-billy-api-auth` header (see APIInterceptor), where it
 * is verified against the project's JWKS.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly sessionSignal = signal<Session | null>(null);

  /**
   * Emits the current Supabase user (or null) once the session is resolved and
   * again on every auth change. Components that previously subscribed to
   * `authState(this.auth)` use this as a drop-in "auth is ready / changed"
   * trigger.
   */
  private readonly userSubject = new BehaviorSubject<User | null>(null);
  readonly authState$ = this.userSubject.asObservable();

  constructor(
    private readonly supabase: SupabaseService,
    private readonly router: Router,
  ) {
    // Restore any persisted session, then keep our local copy in sync with
    // sign-in / sign-out / token-refresh events.
    this.supabase.client.auth.getSession().then(({ data }) => this.setSession(data.session));
    this.supabase.client.auth.onAuthStateChange((_event, session) => this.setSession(session));
  }

  private setSession(session: Session | null): void {
    this.sessionSignal.set(session);
    this.userSubject.next(session?.user ?? null);
  }

  get currentUser(): User | null {
    return this.sessionSignal()?.user ?? null;
  }

  isAuthenticated(): boolean {
    return this.sessionSignal() != null;
  }

  /**
   * Returns the current access token, awaiting client initialization so it is
   * reliable even on the very first request after a page load. supabase-js
   * refreshes the token in the background, so this stays valid.
   */
  async getValidToken(): Promise<string | null> {
    const { data } = await this.supabase.client.auth.getSession();
    return data.session?.access_token ?? null;
  }

  signIn(email: string, password: string) {
    return this.supabase.client.auth.signInWithPassword({ email, password });
  }

  async signOut(): Promise<void> {
    await this.supabase.client.auth.signOut();
    this.setSession(null);
    await this.router.navigate(['/login']);
  }

  sendPasswordReset(email: string) {
    return this.supabase.client.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/auth/callback`,
    });
  }

  updatePassword(password: string) {
    return this.supabase.client.auth.updateUser({ password });
  }
}
